// @flow
import * as React from 'react';
import { Box, Button, ButtonGroup, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  ActionItemsByClientIdDocument,
  useSaveActionItemMutation
} from '../../api';
import { AgentSelector, ClientSelector } from '../../../features';
import { FormikDateInput, FormikTextInput } from '@coverright/ui/inputs';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { covertDateToSeverFormat } from '@coverright/utils';
import { ActionItemInput } from '@coverright/data-access/types/enrollment';

type Props = {
  initialValues?: ActionItemInput & {clientName?: string | null};
  onSubmit: () => void;
};
export const NextActionForm = ({initialValues, onSubmit}: Props) => {
  const formik = useFormik({
    initialValues: {
      assigneeAgentId: initialValues?.assigneeAgentId || '',
      clientId: initialValues?.clientId || '',
      done: initialValues?.done || false,
      title: initialValues?.title || '',
      id: initialValues?.id,
      dueDate: initialValues?.dueDate || moment().format('L'),
    },
    validationSchema: yup.object(schemaFields),
    validateOnChange: true,
    onSubmit: async values => {
      if (formik.isValid) {
        await save({
          variables: {
            input: {
              ...values,
              dueDate: covertDateToSeverFormat(formik.values.dueDate),
            }
          }
        })
      }
    }
  })

  const [save, {loading}] = useSaveActionItemMutation({
    onCompleted: data => onSubmit(),
    refetchQueries: [{
      query: ActionItemsByClientIdDocument,
      variables: { clientId: initialValues?.clientId || formik.values.clientId }
    }]
  })

  return (
    <Stack component={'form'} gap={2} onSubmit={formik.handleSubmit}>
      <Stack gap={2} direction={'row'} alignItems={'flex-start'}>
        <AgentSelector
          value={formik.values.assigneeAgentId}
          onChange={(agentId) => {
            formik.setFieldValue("assigneeAgentId", agentId);
          }}
          error={formik.touched.assigneeAgentId && Boolean(formik.errors.assigneeAgentId) ? formik.errors.assigneeAgentId as string : undefined}/>

        <ClientSelector value={initialValues?.clientId ? { clientId: initialValues?.clientId, firstName: initialValues?.clientName} : undefined}
                        label={'Client'}
                        error={formik.touched.clientId ? formik.errors.clientId as string : undefined}
                        disabled={!!initialValues?.clientName}
                        onChange={value => formik.setFieldValue("clientId", value?.clientId)} />
      </Stack>

      <FormikTextInput
        formik={formik}
        name={'title'}
        fullWidth
        multiline
        hideTick
        autoFocus
        rows={3}
        label="Task"
        placeholder={'Enter task content'}
        value={formik.values.title}
        inputProps={{
          onKeyDown: (e) => {
            if (e.keyCode === 13 && e.ctrlKey) {
              e.preventDefault();
              formik.submitForm();
            }
          },
        }}
        onChange={e => formik.setFieldValue("title", e.target.value as string)}
      />

      <Stack gap={2} direction={'row'} alignItems={'flex-end'}>
        <Stack gap={2} flex={1}>
          <Box sx={{flex: 1}}>
            <FormikDateInput formik={formik} name={'dueDate'} />
          </Box>

          {initialValues?.id && <Stack gap={2} direction={'row'} alignItems={'center'}>
            <Typography fontSize={14} color={'#4B5563'}><b>Completed</b></Typography>
            <ButtonGroup>
              <Button onClick={() => {
                formik.setFieldValue('done', false);
              }} size={'small'}
                      color={'error'}
                      variant={!formik.values.done ? 'contained' : 'outlined'}>No</Button>
              <Button onClick={() => {
                formik.setFieldValue('done', true);
              }} size={'small'}
                      color={'success'}
                      variant={formik.values.done ? 'contained' : 'outlined'}>Yes</Button>
            </ButtonGroup>
          </Stack>}
        </Stack>

        <Stack gap={2} flex={1} direction={'row'} alignItems={'flex-end'} justifyContent={'flex-end'}>
          <Button onClick={() => onSubmit()} variant={'outlined'}>
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            type={'submit'}
            variant={'contained'}
            onClick={() => console.log(formik.errors)}
          >
            Save
          </LoadingButton>
        </Stack>
      </Stack>



    </Stack>
  );
};

const schemaFields = {
  title: yup.string().required('This field is required'),
  assigneeAgentId: yup.string().nullable(true),
  clientId: yup.string().required('This field is required'),
  dueDate: yup.string().nullable(true),
  done: yup.boolean().required('This field is required'),
}




